import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Paper,
  Grid,
} from '@mui/material';
import {
  EmojiEvents,
  Search,
  Add,
  Edit,
  Delete,
  Star,
  Info,
  CheckCircle,
  Cancel,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import performerService, { Performer } from '../../services/performerService';
import storeService from '../../services/storeService';
import userService from '../../services/userService';
import { Store } from '../../types';
import { User } from '../../types';
import { useAuth } from '../../hooks/useAuth';
import ProfileAvatar from '../../components/common/ProfileAvatar';

const PerformersPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [existingPerformer, setExistingPerformer] = useState<Performer | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingExisting, setLoadingExisting] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stores, setStores] = useState<Store[]>([]);
  const [loadingStores, setLoadingStores] = useState(true);
  const [users, setUsers] = useState<User[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isStoreWise, setIsStoreWise] = useState(false);
  const [selectedStoreId, setSelectedStoreId] = useState<string>('');
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [score, setScore] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  useEffect(() => {
    loadExistingPerformer();
    loadStores();
  }, []);

  useEffect(() => {
    if (searchQuery.length >= 2) {
      const timeoutId = setTimeout(() => {
        searchUsers();
      }, 300);
      return () => clearTimeout(timeoutId);
    } else {
      setUsers([]);
    }
  }, [searchQuery, selectedStoreId, isStoreWise]);

  const loadExistingPerformer = async () => {
    try {
      setLoadingExisting(true);
      const storeId = user?.store?.id;
      const performer = await performerService.getPerformerOfTheWeek(storeId);
      setExistingPerformer(performer);
    } catch (err: any) {
      if (err.response?.status !== 404) {
        console.error('Error loading performer:', err);
      }
      setExistingPerformer(null);
    } finally {
      setLoadingExisting(false);
      setLoading(false);
    }
  };

  const loadStores = async () => {
    try {
      setLoadingStores(true);
      const response = await storeService.getStores({ limit: 1000 });
      setStores(response.stores || []);
    } catch (err: any) {
      console.error('Error loading stores:', err);
    } finally {
      setLoadingStores(false);
    }
  };

  const searchUsers = async () => {
    try {
      setLoadingUsers(true);
      const params: any = {
        search: searchQuery,
        limit: 50,
      };
      if (isStoreWise && selectedStoreId) {
        params.store_id = selectedStoreId;
      }
      const response = await userService.getUsers(params);
      setUsers(response.users || []);
    } catch (err: any) {
      console.error('Error searching users:', err);
      setUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleCreatePerformer = async () => {
    if (!selectedUserId) {
      setError('Please select a user');
      return;
    }

    const scoreNum = score ? parseInt(score) : undefined;
    if (scoreNum !== undefined && (scoreNum < 0 || scoreNum > 100)) {
      setError('Score must be between 0 and 100');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await performerService.createPerformer({
        user_id: selectedUserId,
        store_id: isStoreWise && selectedStoreId ? selectedStoreId : null,
        score: scoreNum,
        description: description || undefined,
      });

      await loadExistingPerformer();
      setCreateDialogOpen(false);
      resetForm();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create performer');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeletePerformer = async () => {
    if (!existingPerformer) return;

    setDeleting(true);
    setError(null);

    try {
      await performerService.deletePerformer(existingPerformer.id);
      setExistingPerformer(null);
      setDeleteDialogOpen(false);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete performer');
    } finally {
      setDeleting(false);
    }
  };

  const resetForm = () => {
    setSelectedUserId('');
    setScore('');
    setDescription('');
    setSearchQuery('');
    setUsers([]);
    setSelectedStoreId('');
    setIsStoreWise(false);
    setError(null);
  };

  const handleEditClick = () => {
    if (existingPerformer) {
      navigate('/dashboard');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" component="h1" sx={{ color: '#081122', fontWeight: 600, mb: 1 }}>
            Performer of the Week
          </Typography>
          <Typography variant="body1" sx={{ color: '#666666' }}>
            Announce and manage the performer of the week
          </Typography>
        </Box>
        {!existingPerformer && (user?.role === 'super_admin' || user?.role === 'cluster_level_manager') && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setCreateDialogOpen(true)}
          >
            Announce Performer
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loadingExisting ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      ) : existingPerformer ? (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card sx={{
              borderRadius: 3,
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
              background: user?.id === existingPerformer.user_id
                ? 'linear-gradient(135deg, #FFB347 0%, #FF8C00 100%)'
                : 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
              color: 'white',
            }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{
                      bgcolor: 'rgba(255,255,255,0.2)',
                      borderRadius: 2,
                      p: 1.5,
                    }}>
                      <EmojiEvents sx={{ fontSize: 32 }} />
                    </Box>
                    <Box>
                      <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                        Current Performer of the Week
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        {existingPerformer.week_start_date} - {existingPerformer.week_end_date}
                      </Typography>
                    </Box>
                  </Box>
                  {(user?.role === 'super_admin' || user?.role === 'cluster_level_manager') && (
                    <Box>
                      <IconButton
                        onClick={handleEditClick}
                        sx={{ color: 'white', mr: 1 }}
                        title="Edit Performer"
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        onClick={() => setDeleteDialogOpen(true)}
                        sx={{ color: 'white' }}
                        title="Delete Performer"
                      >
                        <Delete />
                      </IconButton>
                    </Box>
                  )}
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mt: 3 }}>
                  <ProfileAvatar
                    imageUrl={existingPerformer.user.profile_image_url}
                    fullName={`${existingPerformer.user.first_name} ${existingPerformer.user.last_name}`}
                    firstName={existingPerformer.user.first_name}
                    lastName={existingPerformer.user.last_name}
                    sx={{ width: 80, height: 80, bgcolor: 'rgba(255,255,255,0.3)' }}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                      {`${existingPerformer.user.first_name} ${existingPerformer.user.last_name}`}
                    </Typography>
                    {existingPerformer.user.employee_id && (
                      <Typography variant="body2" sx={{ opacity: 0.9, mb: 0.5 }}>
                        {existingPerformer.user.employee_id}
                      </Typography>
                    )}
                    {existingPerformer.store && (
                      <Typography variant="body2" sx={{ opacity: 0.8, mb: 1 }}>
                        {existingPerformer.store.name}
                      </Typography>
                    )}
                    <Chip
                      icon={<Star sx={{ color: '#FFD700' }} />}
                      label={`Score: ${existingPerformer.score}`}
                      sx={{
                        bgcolor: 'rgba(255,255,255,0.25)',
                        color: 'white',
                        border: '1px solid rgba(255,255,255,0.3)',
                      }}
                    />
                  </Box>
                </Box>

                {existingPerformer.description && (
                  <Box sx={{ mt: 3, p: 2, bgcolor: 'rgba(255,255,255,0.15)', borderRadius: 2 }}>
                    <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                      {existingPerformer.description}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      ) : (
        <Card sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)' }}>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <EmojiEvents sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
            <Typography variant="h6" sx={{ color: '#666', mb: 1 }}>
              No Performer of the Week
            </Typography>
            <Typography variant="body2" sx={{ color: '#999', mb: 3 }}>
              No performer has been announced for this week yet.
            </Typography>
            {(user?.role === 'super_admin' || user?.role === 'cluster_level_manager') && (
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => setCreateDialogOpen(true)}
              >
                Announce Performer
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Create Performer Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => {
          setCreateDialogOpen(false);
          resetForm();
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Announce Performer of the Week</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {/* Scope Selection */}
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Scope</InputLabel>
              <Select
                value={isStoreWise ? 'store' : 'overall'}
                onChange={(e) => {
                  setIsStoreWise(e.target.value === 'store');
                  setSelectedStoreId('');
                  setSelectedUserId('');
                  setUsers([]);
                  setSearchQuery('');
                }}
              >
                <MenuItem value="overall">Overall (All Stores)</MenuItem>
                <MenuItem value="store">Store-wise</MenuItem>
              </Select>
            </FormControl>

            {/* Store Selection */}
            {isStoreWise && (
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel>Select Store</InputLabel>
                <Select
                  value={selectedStoreId}
                  onChange={(e) => {
                    setSelectedStoreId(e.target.value);
                    setSelectedUserId('');
                    setUsers([]);
                    setSearchQuery('');
                  }}
                  disabled={loadingStores}
                >
                  <MenuItem value="">All Stores</MenuItem>
                  {stores.map((store) => (
                    <MenuItem key={store.id} value={store.id}>
                      {store.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* User Search */}
            <TextField
              fullWidth
              label="Search User"
              placeholder="Search by name or employee ID"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: <Search sx={{ mr: 1, color: '#666' }} />,
              }}
            />

            {loadingUsers && (
              <Box display="flex" justifyContent="center" sx={{ mb: 2 }}>
                <CircularProgress size={24} />
              </Box>
            )}

            {users.length > 0 && (
              <Paper sx={{ maxHeight: 200, overflow: 'auto', mb: 2 }}>
                {users.map((userItem) => (
                  <Box
                    key={userItem.id}
                    onClick={() => setSelectedUserId(userItem.id)}
                    sx={{
                      p: 2,
                      cursor: 'pointer',
                      bgcolor: selectedUserId === userItem.id ? 'primary.light' : 'transparent',
                      '&:hover': { bgcolor: 'action.hover' },
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <ProfileAvatar
                        imageUrl={userItem.profile_image_url}
                        fullName={`${userItem.first_name} ${userItem.last_name}`}
                        firstName={userItem.first_name}
                        lastName={userItem.last_name}
                        sx={{ width: 40, height: 40 }}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {`${userItem.first_name} ${userItem.last_name}`}
                        </Typography>
                        {userItem.employee_id && (
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {userItem.employee_id}
                          </Typography>
                        )}
                      </Box>
                      {selectedUserId === userItem.id && (
                        <CheckCircle color="primary" />
                      )}
                    </Box>
                  </Box>
                ))}
              </Paper>
            )}

            {/* Score */}
            <TextField
              fullWidth
              label="Score (0-100, optional)"
              type="number"
              value={score}
              onChange={(e) => setScore(e.target.value)}
              sx={{ mb: 2 }}
              inputProps={{ min: 0, max: 100 }}
              helperText="Enter a score between 0 and 100"
            />

            {/* Description */}
            <TextField
              fullWidth
              label="Description (optional)"
              multiline
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter description or achievement details"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setCreateDialogOpen(false);
              resetForm();
            }}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreatePerformer}
            variant="contained"
            disabled={submitting || !selectedUserId}
            startIcon={submitting ? <CircularProgress size={20} /> : null}
          >
            {submitting ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Performer</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the current performer of the week? You will be able to create a new one after deletion.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeletePerformer}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={20} /> : <Delete />}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PerformersPage;

